// Проверка на репутация по телефон или име.
// БЕЗ вход: показва само дали човекът съществува + рейтинг (без текста).
// СЛЕД вход: пълните ревюта и историята.

import React, { useEffect, useState } from 'react';
import { Linking, Platform, Pressable, Text, View } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { api } from '../api';
import { useAuth } from '../AuthContext';
import { showError } from '../toast';
import { track } from '../analytics';
import { Badge, Button, Card, COLORS, Input, Label, Muted, Row, Screen, Title } from '../ui';
import { getLocale, t } from '../i18n';

// 90 дни → „3 месеца“, 45 дни → „45 дни“
export const promoLabel = (days: number) =>
  days % 30 === 0 ? (days / 30 === 1 ? t('1 месец') : t('{n} месеца', { n: days / 30 })) : t('{n} дни', { n: days });

const stars = (avg: number | null) => (avg ? `★ ${avg.toFixed(1)}` : t('няма ревюта'));

export default function ReputationScreen() {
  const { user } = useAuth();
  const loggedIn = !!user;
  const navigation = useNavigation<any>();
  const [q, setQ] = useState('');
  const [showExample, setShowExample] = useState(false);

  // Невлязъл → регистрация. На уеб оставяме hash, за да може App.tsx след
  // регистрацията да го заведе направо в „Минали наематели“ (за отзив).
  const goRegister = (intent: 'review' | 'see') => {
    track('register_start', { from: 'reputation', intent });
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.location.hash = intent === 'review' ? '#review-tenant' : '#register';
    }
    navigation.navigate('Register');
  };
  const [results, setResults] = useState<any[] | null>(null);
  const [detail, setDetail] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<{ reviewCount: number; reviewerCount: number; reviews7d: number; searches7d: number } | null>(null);
  const [promo, setPromo] = useState<{ trialDays: number; maxAccounts: number; remaining: number } | null>(null);

  // Социално доказателство за посетители от реклама — показва се само ако
  // числото вече е убедително (иначе по-скоро отблъсква).
  useEffect(() => {
    if (loggedIn) return;
    api('/reputation/stats').then(setStats).catch(() => {});
    api('/auth/promotion').then((r) => setPromo(r.promotion)).catch(() => {});
  }, [loggedIn]);

  const search = async () => {
    setLoading(true);
    setDetail(null);
    try {
      const r = await api(`/reputation/search?q=${encodeURIComponent(q)}`);
      setResults(r.results);
      track('reputation_search', { logged_in: loggedIn, found: r.results.length > 0 });
    } catch (e: any) {
      showError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (id: string) => {
    if (!loggedIn) return;
    try {
      setDetail(await api(`/reputation/reviews/${id}`));
    } catch (e: any) {
      showError(e.message);
    }
  };

  return (
    <Screen>
      <Title>{t('Провери наемател')}</Title>
      <Muted>
        {loggedIn
          ? t('Наемател, наемодател, пропърти мениджър или майстор — по телефон или име.')
          : t('Въведете телефонния номер на кандидата. Безплатно, без регистрация.')}
      </Muted>

      {/* Полето е първото нещо на екрана — човекът е дошъл да провери номер. */}
      <View style={{ flexDirection: 'row', gap: 8, marginTop: 10, alignItems: 'center' }}>
        <View style={{ flex: 1 }}>
          <Input value={q} onChangeText={setQ} placeholder={t('+359… или име')} autoCapitalize="none" onSubmitEditing={search} onFocus={() => track('reputation_focus')} />
        </View>
        <Button
          title={t('Търси')}
          onPress={search}
          disabled={q.trim().length < 3}
          loading={loading}
          style={{ marginTop: 0, flexShrink: 0 }}
        />
      </View>

      {/* Броячи — колко проверки и колко отзива има; винаги видими под полето. */}
      {!loggedIn && stats && (stats.searches7d > 0 || stats.reviewCount > 0) && (
        <View style={{ flexDirection: 'row', gap: 8, marginTop: 12 }}>
          {stats.searches7d > 0 && (
            <View style={{ flex: 1, backgroundColor: COLORS.card, borderRadius: 12, padding: 12, alignItems: 'center' }}>
              <Text style={{ fontSize: 22, fontWeight: '800', color: COLORS.text }}>{stats.searches7d}</Text>
              <Muted style={{ textAlign: 'center' }}>{t('проверки тази седмица')}</Muted>
            </View>
          )}
          {stats.reviewCount > 0 && (
            <View style={{ flex: 1, backgroundColor: COLORS.card, borderRadius: 12, padding: 12, alignItems: 'center' }}>
              <Text style={{ fontSize: 22, fontWeight: '800', color: COLORS.text }}>{stats.reviewCount}</Text>
              <Muted style={{ textAlign: 'center' }}>{stats.reviewerCount === 1 ? t('отзива от 1 наемодател') : t('отзива от {n} наемодатели', { n: stats.reviewerCount })}</Muted>
            </View>
          )}
        </View>
      )}

      {!loggedIn && results === null && (
        <Muted style={{ marginTop: 10, fontSize: 12 }}>
          {t('🔒 Отзиви само от потвърдени по телефон наемодатели · без имена и адреси, само оценка към номер · наемателят вижда отзивите за себе си и може да отговори.')}
        </Muted>
      )}

      {!loggedIn && results === null && (
        // Примерът е сгънат — който иска да види как изглежда резултат, го отваря.
        <>
          <Pressable onPress={() => setShowExample((v) => !v)} hitSlop={8} style={{ marginTop: 12 }}>
            <Text style={{ color: COLORS.primary, fontWeight: '700' }}>
              {showExample ? t('▾ Пример как изглежда резултат') : t('▸ Пример как изглежда резултат')}
            </Text>
          </Pressable>
          {showExample && (
            <Card>
              <Badge text={t('Пример — така изглежда проверка в Livupp')} color={COLORS.muted} />
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                <Text style={{ fontSize: 16, fontWeight: '700', color: COLORS.text }}>{t('Наемател · +359 88 4•• •••')}</Text>
                <Text style={{ color: '#F59E0B', fontWeight: '700' }}>★ 1.0</Text>
              </View>
              <View style={{ backgroundColor: COLORS.dangerSoft, borderRadius: 10, padding: 10, marginTop: 8 }}>
                <Text style={{ color: COLORS.danger, fontWeight: '700' }}>{t('Неплатен наем — 2 наемодатели')}</Text>
              </View>
              <View style={{ backgroundColor: COLORS.dangerSoft, borderRadius: 10, padding: 10, marginTop: 6 }}>
                <Text style={{ color: COLORS.danger, fontWeight: '700' }}>{t('Щети по имота')}</Text>
              </View>
              <Muted style={{ marginTop: 8, fontStyle: 'italic' }}>
                {t('„Изчезна с два наема и остави апартамента в лошо състояние.“ — наемодател, Пловдив')}
              </Muted>
              <Muted style={{ marginTop: 8 }}>{t('Отзивите пишат реални наемодатели. Виждат се безплатно след регистрация.')}</Muted>
            </Card>
          )}
        </>
      )}

      {!loggedIn && results === null && (
        <Card>
          <Text style={{ fontSize: 18, fontWeight: '800', color: COLORS.text }}>{t('Имали сте свой „Георги“?')}</Text>
          <Muted style={{ marginTop: 6 }}>
            {t('Добавете го за 1 минута — така следващият наемодател ще знае навреме.')}
          </Muted>
          <Button title={t('Добави наемател — безплатно')} onPress={() => goRegister('review')} />
          {promo && (
            <View style={{ backgroundColor: '#FFF4D6', borderRadius: 12, padding: 10, marginTop: 10 }}>
              <Text style={{ color: '#7a3e00', fontWeight: '700', textAlign: 'center' }}>
                {t('🎁 Първите {n} регистрирани — {period} безплатно · остават {left} места', { n: promo.maxAccounts, period: promoLabel(promo.trialDays), left: promo.remaining })}
              </Text>
            </View>
          )}
          <Pressable onPress={() => navigation.navigate('Login')}>
            <Muted style={{ textAlign: 'center', marginTop: 10 }}>{t('Вече имате акаунт? Вход')}</Muted>
          </Pressable>
        </Card>
      )}

      {!loggedIn && results === null && (
        <Button
          title={t('Виж повече за Livupp →')}
          variant="secondary"
          onPress={() => Linking.openURL('https://livupp.com')}
        />
      )}

      {results && results.length === 0 && (
        <Card>
          <Text style={{ fontSize: 16, fontWeight: '700', color: COLORS.text }}>{t('Още няма отзиви за този номер.')}</Text>
          {loggedIn ? (
            <Muted>{t('Няма намерен човек с това търсене.')}</Muted>
          ) : (
            <>
              <Muted style={{ marginTop: 6 }}>
                {t('Имали сте проблем с този наемател? Регистрирайте се безплатно и оставете първия отзив — така предупреждавате следващия наемодател.')}
              </Muted>
              <Button title={t('Добави отзив — безплатно')} onPress={() => goRegister('review')} />
              <Pressable onPress={() => navigation.navigate('Login')}>
                <Muted style={{ textAlign: 'center', marginTop: 10 }}>{t('Вече имате акаунт? Вход')}</Muted>
              </Pressable>
            </>
          )}
        </Card>
      )}

      {!loggedIn && results && results.length > 0 && (
        <Card>
          <Text style={{ fontSize: 16, fontWeight: '700', color: COLORS.text }}>
            {results.some((r) => r.hasReviews) ? t('Има отзиви за този човек.') : t('Този човек е в Livupp.')}
          </Text>
          <Muted style={{ marginTop: 6 }}>
            {t('Регистрирайте се безплатно (1 минута), за да видите оценката и какво пишат наемодателите — или да добавите свой отзив.')}
          </Muted>
          <Button title={t('Виж отзивите — безплатно')} onPress={() => goRegister('see')} />
          <Pressable onPress={() => goRegister('review')}>
            <Muted style={{ textAlign: 'center', marginTop: 10 }}>{t('Искам да добавя отзив за този наемател →')}</Muted>
          </Pressable>
          <Pressable onPress={() => navigation.navigate('Login')}>
            <Muted style={{ textAlign: 'center', marginTop: 8 }}>{t('Вече имате акаунт? Вход')}</Muted>
          </Pressable>
        </Card>
      )}

      {!detail &&
        results?.map((r) => (
          <Pressable key={r.id} onPress={() => openDetail(r.id)} disabled={!loggedIn}>
            <Card>
              <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 16, fontWeight: '600', color: COLORS.text }}>
                    {r.firstName} {r.lastName}
                  </Text>
                  <Badge text={t('Съществува като {role}', { role: r.roleLabel })} color={COLORS.success} />
                </View>
                <View style={{ alignItems: 'flex-end' }}>
                  {loggedIn ? (
                    <>
                      <Text style={{ color: r.avgRating ? '#F59E0B' : COLORS.muted, fontWeight: '700' }}>
                        {stars(r.avgRating)}
                      </Text>
                      <Muted>{t('{n} ревюта', { n: r.reviewCount })}</Muted>
                    </>
                  ) : (
                    // Без вход: казваме само ЧЕ има ревю — без звезди и без брой
                    <Badge
                      text={r.hasReviews ? t('Има оставено ревю') : t('Няма ревюта')}
                      color={r.hasReviews ? '#F59E0B' : COLORS.muted}
                    />
                  )}
                </View>
              </View>
              {loggedIn ? (
                <Muted>{t('Докоснете за пълните ревюта →')}</Muted>
              ) : (
                <Muted>
                  {r.hasReviews
                    ? t('Влезте, за да видите оценката и какво пишат ревютата.')
                    : t('Влезте, за да видите пълния профил.')}
                </Muted>
              )}
            </Card>
          </Pressable>
        ))}

      {detail && (
        <>
          <Button title={t('← Обратно към резултатите')} variant="secondary" onPress={() => setDetail(null)} />
          <Card>
            <Text style={{ fontSize: 18, fontWeight: '700', color: COLORS.text }}>
              {detail.user.firstName} {detail.user.lastName}
            </Text>
            <Badge text={detail.user.roleLabel} />
            <Row left={t('Среден рейтинг')} right={stars(detail.avgRating)} />
            <Row left={t('Брой ревюта')} right={String(detail.reviewCount)} />
            {detail.tenancyHistory?.length > 0 && (
              <>
                <Label>{t('Квартири')}</Label>
                {detail.tenancyHistory.map((h: any, i: number) => (
                  <Muted key={i}>
                    {h.city}: {t('{n} мес.', { n: h.months })}{h.current ? ` ${t('(в момента)')}` : ''}
                  </Muted>
                ))}
              </>
            )}
          </Card>

          {detail.reviews.length === 0 && (
            <Card>
              <Muted>{t('Още няма ревюта за този човек.')}</Muted>
            </Card>
          )}
          {detail.reviews.map((rev: any, i: number) => (
            <Card key={i}>
              <Text style={{ color: '#F59E0B', fontWeight: '700' }}>{'★'.repeat(rev.rating)}{'☆'.repeat(5 - rev.rating)}</Text>
              {rev.comment ? <Text style={{ color: COLORS.text, marginTop: 4 }}>{rev.comment}</Text> : <Muted>{t('(без коментар)')}</Muted>}
              <Muted>
                — {rev.from} ({rev.fromRole}) · {new Date(rev.createdAt).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG')}
              </Muted>
            </Card>
          ))}
        </>
      )}
    </Screen>
  );
}
